/**
 * Zod schema + form-values type for the Contact Details tab (PROD-728).
 *
 * Mirrors Flutter `UpdatePatientContactRequest` + `PatientAddressModel`:
 *   - `isPreferrable` is a 0/1/2 int enum (email / primary / secondary),
 *     NOT a boolean — see `patient-api.ts` doc-block.
 *   - Addresses carry `isDeleted` for soft-delete; rows toggled as deleted
 *     stay in the array until the next save, then the backend tombstones
 *     them server-side.
 *   - UI-only flags `isEdit` / `isManualAddress` are scoped to the form,
 *     never sent to the backend.
 *
 * Required-field policy mirrors Flutter:
 *   - ownership (mandatory)
 *   - primaryContact (mandatory)
 *   - email (mandatory + format-checked)
 *     Flutter `contact_details_form.dart:109-112` runs `isEmail(value)` on
 *     every change; `isEmail("")` is false, so an empty email fails the same
 *     check as a malformed one and surfaces `S.of(context).not_a_valid_email`
 *     ("Not a valid Email"). The save-guard also gates on
 *     `!isBlank(patient.email)` (`create_edit_patient_bloc.dart:230`). A single
 *     verbatim string therefore covers both the empty-required and the
 *     invalid-format case — matching Flutter exactly.
 *
 * Validation rules:
 *   - secondaryContact must NOT equal primaryContact (Flutter parity)
 *   - location name must be unique within the address list
 *   - isPreferrable defaults to 1 (primary contact) when none set
 *
 * Flutter ref: `contact_details_form.dart`, `residential_address_form.dart`,
 *              `patient_address_model.dart`.
 */

import { z } from 'zod';

/** `isPreferrable` enum — keeps the int contract explicit. */
export const PREFERRED_EMAIL = 0;
export const PREFERRED_PRIMARY = 1;
export const PREFERRED_SECONDARY = 2;
export type PreferredContact =
  | typeof PREFERRED_EMAIL
  | typeof PREFERRED_PRIMARY
  | typeof PREFERRED_SECONDARY;

export const addressFormSchema = z.object({
  /** Server-assigned id. Null on rows added in this session. */
  contactId: z.string().nullable(),
  locationName: z.string().trim().min(1, 'Location name is required'),
  building: z.string().nullable(),
  apartment: z.string().nullable(),
  /** Free-text street address (the autocomplete output). */
  address: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  /** Latitude as a string — matches the Flutter wire convention. */
  latitude: z.string().nullable(),
  longitude: z.string().nullable(),
  notes: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  /** Soft-delete flag — keeps the row in the array until save. */
  isDeleted: z.boolean(),
  /** UI-only: row is in inline-edit mode. NOT shipped. */
  isEdit: z.boolean(),
  /** UI-only: user opted out of autocomplete on this row. NOT shipped. */
  isManualAddress: z.boolean(),
});

export type AddressFormValues = z.infer<typeof addressFormSchema>;

// Email is mandatory on patient create — matches Flutter
// `contact_details_form.dart`, where the field carries `isMandatory: true`
// and `isEmail(value)` (which rejects "") gates the stored value, while the
// save-guard requires `!isBlank(patient.email)`. `isEmail("")` is false, so the
// empty-required and invalid-format cases share one verbatim message:
// `S.of(context).not_a_valid_email` → "Not a valid Email"
// (intl_en.arb:77; used at contact_details_form.dart:112).
const requiredEmail = z
  .string()
  .refine(
    (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v ?? ''),
    'Not a valid Email',
  );

export const contactDetailsSchema = z
  .object({
    patientId: z.string().nullable(),
    ownership: z.string().min(1, 'Ownership is required'),
    email: requiredEmail,
    primaryContact: z.string().trim().min(1, 'Primary contact is required'),
    secondayContact: z.string().nullable(),
    isPreferrable: z.union([
      z.literal(PREFERRED_EMAIL),
      z.literal(PREFERRED_PRIMARY),
      z.literal(PREFERRED_SECONDARY),
    ]),
    addresses: z.array(addressFormSchema),
  })
  .superRefine((data, ctx) => {
    if (
      data.secondayContact &&
      data.secondayContact.trim().length > 0 &&
      data.secondayContact === data.primaryContact
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['secondayContact'],
        message: 'Phone number already used as primary contact',
      });
    }

    // Active (non-deleted) location names must be unique.
    const seen = new Map<string, number>();
    data.addresses.forEach((row, i) => {
      if (row.isDeleted) return;
      const key = row.locationName.trim().toLowerCase();
      if (!key) return;
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['addresses', i, 'locationName'],
          message: 'Location name already used',
        });
      } else {
        seen.set(key, i);
      }
    });
  });

export type ContactDetailsFormValues = z.infer<typeof contactDetailsSchema>;

export function defaultContactValues(
  patientId: string | null,
): ContactDetailsFormValues {
  return {
    patientId,
    ownership: '',
    email: '',
    primaryContact: '',
    secondayContact: null,
    isPreferrable: PREFERRED_PRIMARY,
    addresses: [],
  };
}

export function defaultAddressRow(): AddressFormValues {
  return {
    contactId: null,
    locationName: '',
    building: null,
    apartment: null,
    address: null,
    city: null,
    country: null,
    latitude: null,
    longitude: null,
    notes: null,
    isDefault: false,
    isActive: true,
    isDeleted: false,
    isEdit: true,
    isManualAddress: false,
  };
}
